import { useState, useEffect, useRef, useCallback } from "react";
import { Lightning, Warning, CheckCircle, WifiHigh, WifiSlash, Gauge, Info } from "@phosphor-icons/react";

export default function VoltageDetectorPage() {
  const [supported, setSupported] = useState(null);
  const [active, setActive] = useState(false);
  const [readings, setReadings] = useState({ x: 0, y: 0, z: 0 });
  const [magnitude, setMagnitude] = useState(0);
  const [peak, setPeak] = useState(0);
  const [baseline, setBaseline] = useState(0);
  const [calibrated, setCalibrated] = useState(false);
  const [alertLevel, setAlertLevel] = useState("none"); // none, low, medium, high
  const [history, setHistory] = useState([]);
  const sensorRef = useRef(null);
  const baselineRef = useRef([]);
  const historyRef = useRef([]);

  // Thresholds for AC detection (µT above baseline)
  const THRESHOLD_LOW = 5;
  const THRESHOLD_MEDIUM = 15;
  const THRESHOLD_HIGH = 40;

  const [sensorMode, setSensorMode] = useState(""); // "magnetometer", "orientation", ""

  useEffect(() => {
    // Check for Magnetometer API first, then deviceorientation fallback
    if ("Magnetometer" in window) {
      setSupported(true);
      setSensorMode("magnetometer");
    } else if ("DeviceOrientationEvent" in window) {
      setSupported(true);
      setSensorMode("orientation");
    } else {
      setSupported(false);
    }
    return () => {
      if (sensorRef.current) {
        try { sensorRef.current.stop(); } catch {}
      }
      window.removeEventListener("deviceorientation", handleOrientation);
    };
  }, []);

  const calibrate = useCallback(() => {
    baselineRef.current = [];
    setCalibrated(false);
    setPeak(0);
    setHistory([]);
    historyRef.current = [];
    // Collect 30 readings for baseline
    setTimeout(() => {
      if (baselineRef.current.length > 0) {
        const avg = baselineRef.current.reduce((a, b) => a + b, 0) / baselineRef.current.length;
        setBaseline(avg);
        setCalibrated(true);
      }
    }, 2000);
  }, []);

  const handleOrientation = useCallback((event) => {
    // deviceorientation gives alpha (compass heading), beta, gamma
    // We use the compass heading change as a proxy for magnetic field variation
    const alpha = event.alpha || 0;
    const beta = event.beta || 0;
    const gamma = event.gamma || 0;
    // Compute a pseudo-magnitude from orientation sensor values
    // This leverages the magnetometer internally for compass heading
    const mag = Math.sqrt(alpha * alpha + beta * beta + gamma * gamma) / 10;

    setReadings({ x: alpha.toFixed(1), y: beta.toFixed(1), z: gamma.toFixed(1) });
    setMagnitude(mag);

    if (!calibrated && baselineRef.current.length < 30) {
      baselineRef.current.push(mag);
    }
    setPeak(prev => Math.max(prev, mag));
    const now = Date.now();
    historyRef.current = [...historyRef.current.slice(-59), { t: now, v: mag }];
    setHistory([...historyRef.current]);

    const delta = Math.abs(mag - baseline);
    if (calibrated) {
      if (delta >= THRESHOLD_HIGH) setAlertLevel("high");
      else if (delta >= THRESHOLD_MEDIUM) setAlertLevel("medium");
      else if (delta >= THRESHOLD_LOW) setAlertLevel("low");
      else setAlertLevel("none");
    }
  }, [calibrated, baseline]);

  const startSensor = useCallback(async () => {
    if (sensorMode === "magnetometer" && "Magnetometer" in window) {
      try {
        if ("permissions" in navigator) {
          const result = await navigator.permissions.query({ name: "magnetometer" });
          if (result.state === "denied") {
            // Fall back to orientation
            setSensorMode("orientation");
          }
        }
      } catch {}
    }

    const mode = sensorMode || "orientation";

    if (mode === "magnetometer" && "Magnetometer" in window) {
      try {
        const sensor = new window.Magnetometer({ frequency: 30 });
        sensor.addEventListener("reading", () => {
          const x = sensor.x || 0;
          const y = sensor.y || 0;
          const z = sensor.z || 0;
          const mag = Math.sqrt(x * x + y * y + z * z);
          setReadings({ x: x.toFixed(1), y: y.toFixed(1), z: z.toFixed(1) });
          setMagnitude(mag);
          if (!calibrated && baselineRef.current.length < 30) baselineRef.current.push(mag);
          setPeak(prev => Math.max(prev, mag));
          const now = Date.now();
          historyRef.current = [...historyRef.current.slice(-59), { t: now, v: mag }];
          setHistory([...historyRef.current]);
          const delta = Math.abs(mag - baseline);
          if (calibrated) {
            if (delta >= THRESHOLD_HIGH) setAlertLevel("high");
            else if (delta >= THRESHOLD_MEDIUM) setAlertLevel("medium");
            else if (delta >= THRESHOLD_LOW) setAlertLevel("low");
            else setAlertLevel("none");
          }
        });
        sensor.addEventListener("error", () => {
          // Fall back to orientation on error
          setSensorMode("orientation");
          window.addEventListener("deviceorientation", handleOrientation);
        });
        sensor.start();
        sensorRef.current = sensor;
      } catch {
        // Fall back to orientation
        setSensorMode("orientation");
        window.addEventListener("deviceorientation", handleOrientation);
      }
    } else {
      // Use deviceorientation as fallback (uses magnetometer internally for compass)
      // iOS requires permission request
      if (typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function") {
        try {
          const perm = await DeviceOrientationEvent.requestPermission();
          if (perm !== "granted") { setSupported(false); return; }
        } catch { setSupported(false); return; }
      }
      window.addEventListener("deviceorientation", handleOrientation);
      setSensorMode("orientation");
    }

    setActive(true);
    calibrate();
  }, [sensorMode, calibrate, calibrated, baseline, handleOrientation]);

  const stopSensor = useCallback(() => {
    if (sensorRef.current) {
      try { sensorRef.current.stop(); } catch {}
      sensorRef.current = null;
    }
    window.removeEventListener("deviceorientation", handleOrientation);
    setActive(false);
    setAlertLevel("none");
  }, [handleOrientation]);

  // Gauge colors
  const alertConfig = {
    none: { color: "#34C759", label: "NO VOLTAGE DETECTED", bg: "#34C75915" },
    low: { color: "#FFCC00", label: "WEAK EMF DETECTED", bg: "#FFCC0015" },
    medium: { color: "#FF5A00", label: "VOLTAGE NEARBY", bg: "#FF5A0015" },
    high: { color: "#FF0000", label: "LIVE WIRE - CAUTION", bg: "#FF000015" },
  };

  const currentAlert = alertConfig[alertLevel];
  const delta = calibrated ? Math.abs(magnitude - baseline) : 0;
  const gaugePercent = Math.min((delta / THRESHOLD_HIGH) * 100, 100);

  // Mini sparkline
  const sparklineMax = Math.max(...history.map(h => h.v), 1);
  const sparklineMin = Math.min(...history.map(h => h.v), 0);
  const sparklineRange = sparklineMax - sparklineMin || 1;

  // Unsupported browser fallback
  if (supported === false) {
    return (
      <div className="p-4 max-w-lg mx-auto" data-testid="voltage-detector-page">
        <h1 className="text-xl font-black tracking-tight mb-1" style={{ fontFamily: "'Chivo', sans-serif" }}>
          Voltage Detector
        </h1>
        <p className="text-xs text-[#404040] mb-6 font-mono-data">NON-CONTACT EMF SENSOR</p>
        <div className="wp-card text-center py-8">
          <WifiSlash size={48} className="mx-auto mb-3 text-[#E5E5E5]" />
          <h2 className="text-base font-bold mb-2" style={{ fontFamily: "'Chivo', sans-serif" }}>Magnetometer Not Available</h2>
          <p className="text-xs text-[#404040] leading-relaxed max-w-xs mx-auto">
            Your browser or device doesn't support the Magnetometer API. This feature requires:
          </p>
          <ul className="text-xs text-[#404040] mt-3 space-y-1 text-left max-w-xs mx-auto">
            <li className="flex items-start gap-1.5">
              <span className="text-[#002FA7] mt-0.5">-</span>
              Chrome on Android with sensor flags enabled
            </li>
            <li className="flex items-start gap-1.5">
              <span className="text-[#002FA7] mt-0.5">-</span>
              HTTPS connection (secure context)
            </li>
            <li className="flex items-start gap-1.5">
              <span className="text-[#002FA7] mt-0.5">-</span>
              Device with built-in magnetometer
            </li>
          </ul>
          <div className="mt-4 p-3 bg-[#FF5A0010] border border-[#FF5A00] rounded-sm">
            <p className="text-[10px] text-[#404040] leading-relaxed">
              <b className="text-[#FF5A00]">DISCLAIMER:</b> This tool is NOT a certified non-contact voltage tester 
              and must NEVER be used to determine if a circuit is live or de-energized. Always use 
              a UL-listed, CAT-rated voltage tester. Serious injury or death can result from 
              improper voltage testing. WorkPro assumes no liability for any injury, damage, or 
              loss. By using this feature you accept full responsibility for your own safety.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (supported === null) {
    return (
      <div className="p-4 max-w-lg mx-auto flex items-center justify-center h-64" data-testid="voltage-detector-page">
        <div className="animate-spin text-[#002FA7]"><Gauge size={32} /></div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-lg mx-auto" data-testid="voltage-detector-page">
      <h1 className="text-xl font-black tracking-tight mb-1" style={{ fontFamily: "'Chivo', sans-serif" }}>
        Voltage Detector
      </h1>
      <p className="text-xs text-[#404040] mb-4 font-mono-data">NON-CONTACT EMF SENSOR</p>

      {/* Safety Warning */}
      <div className="p-3 bg-[#FF5A0008] border border-[#FF5A00] rounded-sm mb-4">
        <p className="text-xs font-bold text-[#FF5A00] mb-1">DISCLAIMER - READ BEFORE USE</p>
        <p className="text-[10px] text-[#404040] leading-relaxed">
          This EMF detection tool uses your phone's magnetometer for <b>reference purposes only</b>. 
          It is <b>NOT</b> a certified non-contact voltage tester and must <b>NEVER</b> be used to determine 
          if a circuit is live or de-energized. Always use a UL-listed, CAT-rated voltage tester 
          before working on any electrical circuit. Failure to use proper testing equipment can 
          result in serious injury or death. WorkPro and its developers assume <b>no liability</b> for 
          any injury, damage, or loss resulting from reliance on this tool. By using this feature 
          you acknowledge and accept full responsibility for your own safety.
        </p>
      </div>

      {/* Main Gauge */}
      <div
        className="wp-card mb-4 transition-colors duration-300"
        style={{ background: active ? currentAlert.bg : "#FFFFFF", borderColor: active ? currentAlert.color : "#E5E5E5" }}
        data-testid="voltage-gauge"
      >
        {/* Status indicator */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {active ? (
              <WifiHigh size={18} className="text-[#002FA7] animate-pulse" />
            ) : (
              <WifiSlash size={18} className="text-[#404040]" />
            )}
            <span className="font-mono-data text-[10px]">
              {active ? (calibrated ? `SCANNING (${sensorMode === "magnetometer" ? "MAG" : "ORIENT"})` : "CALIBRATING...") : "INACTIVE"}
            </span>
          </div>
          {calibrated && (
            <span className="font-mono-data text-[10px] text-[#404040]">
              BASE: {baseline.toFixed(1)} µT
            </span>
          )}
        </div>

        {/* Big reading */}
        <div className="text-center mb-4">
          <div
            className="text-5xl font-bold tracking-tighter transition-colors duration-300"
            style={{ fontFamily: "'IBM Plex Mono', monospace", color: active ? currentAlert.color : "#E5E5E5" }}
            data-testid="magnitude-reading"
          >
            {active ? delta.toFixed(1) : "---"}
          </div>
          <div className="font-mono-data text-[10px] text-[#404040] mt-1">µT DEVIATION</div>
        </div>

        {/* Gauge bar */}
        <div className="w-full h-3 bg-[#F4F4F5] rounded-sm overflow-hidden mb-2">
          <div
            className="h-full transition-all duration-200 rounded-sm"
            style={{
              width: `${active ? gaugePercent : 0}%`,
              background: active ? currentAlert.color : "#E5E5E5",
            }}
            data-testid="gauge-bar"
          />
        </div>
        <div className="flex justify-between font-mono-data text-[8px] text-[#404040]">
          <span>0</span>
          <span>LOW</span>
          <span>MED</span>
          <span>HIGH</span>
        </div>

        {/* Alert text */}
        {active && calibrated && (
          <div
            className="text-center mt-3 py-2 rounded-sm font-bold text-sm transition-colors duration-300"
            style={{ color: currentAlert.color }}
            data-testid="alert-text"
          >
            {alertLevel === "high" && <Lightning size={16} weight="fill" className="inline mr-1" />}
            {currentAlert.label}
          </div>
        )}
      </div>

      {/* Start/Stop Button */}
      <div className="flex gap-3 mb-4">
        {!active ? (
          <button
            data-testid="start-detector-btn"
            onClick={startSensor}
            className="flex-1 py-3 bg-[#002FA7] text-white text-sm font-bold rounded-sm hover:bg-[#001D6C] transition-colors flex items-center justify-center gap-2"
          >
            <Lightning size={16} /> Start Detector
          </button>
        ) : (
          <>
            <button
              data-testid="stop-detector-btn"
              onClick={stopSensor}
              className="flex-1 py-3 bg-[#FF5A00] text-white text-sm font-bold rounded-sm hover:bg-[#CC4800] transition-colors flex items-center justify-center gap-2"
            >
              Stop
            </button>
            <button
              data-testid="recalibrate-btn"
              onClick={calibrate}
              className="px-4 py-3 border border-[#E5E5E5] text-sm font-bold rounded-sm hover:border-[#0A0A0A] transition-colors"
            >
              Re-Cal
            </button>
          </>
        )}
      </div>

      {/* Detailed Readings */}
      {active && (
        <div className="space-y-3 animate-slide-up">
          {/* XYZ Readings */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { axis: "X", val: readings.x },
              { axis: "Y", val: readings.y },
              { axis: "Z", val: readings.z },
            ].map(({ axis, val }) => (
              <div key={axis} className="wp-card text-center p-2">
                <div className="font-mono-data text-[10px] text-[#404040]">{axis}-AXIS</div>
                <div className="font-mono font-bold text-base text-[#0A0A0A]">{val}</div>
                <div className="font-mono-data text-[8px] text-[#404040]">µT</div>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="wp-card">
              <div className="metric-label">TOTAL FIELD</div>
              <div className="font-mono font-bold text-lg text-[#0A0A0A]">{magnitude.toFixed(1)} µT</div>
            </div>
            <div className="wp-card">
              <div className="metric-label">PEAK</div>
              <div className="font-mono font-bold text-lg text-[#FF5A00]">{peak.toFixed(1)} µT</div>
            </div>
          </div>

          {/* Sparkline */}
          {history.length > 5 && (
            <div className="wp-card" data-testid="emf-sparkline">
              <div className="font-mono-data text-[10px] text-[#404040] mb-2">FIELD STRENGTH OVER TIME</div>
              <svg viewBox="0 0 300 60" className="w-full h-16">
                <polyline
                  fill="none"
                  stroke={currentAlert.color}
                  strokeWidth="1.5"
                  points={history.map((h, i) => {
                    const x = (i / (history.length - 1)) * 300;
                    const y = 55 - ((h.v - sparklineMin) / sparklineRange) * 50;
                    return `${x},${y}`;
                  }).join(" ")}
                />
                {/* Baseline reference */}
                {calibrated && (
                  <line
                    x1="0"
                    y1={55 - ((baseline - sparklineMin) / sparklineRange) * 50}
                    x2="300"
                    y2={55 - ((baseline - sparklineMin) / sparklineRange) * 50}
                    stroke="#E5E5E5"
                    strokeWidth="1"
                    strokeDasharray="4,4"
                  />
                )}
              </svg>
            </div>
          )}

          {/* Tips */}
          <div className="wp-card">
            <div className="flex items-start gap-2">
              <Info size={14} className="text-[#002FA7] shrink-0 mt-0.5" />
              <div className="text-[11px] text-[#404040] leading-relaxed">
                <p className="font-bold text-[#0A0A0A] mb-1">How to use:</p>
                <ul className="space-y-0.5">
                  <li>1. Tap <b>Start Detector</b> and hold phone still for 2s calibration</li>
                  <li>2. Slowly move phone near wires, outlets, or panels</li>
                  <li>3. Rising readings indicate electromagnetic field (possible live voltage)</li>
                  <li>4. Tap <b>Re-Cal</b> if you move to a new area</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
